import { useEffect, useRef } from "react"

import { useDraftValue } from "./useDraftValue"

import useDebounce from "@/utils/useDebounce"

const COMMIT_DEBOUNCE_MS = 300

const isInvalidDateRange = (from: string | undefined, to: string | undefined): boolean =>
  Boolean(from && to && to < from)

interface UseDraftDateRangeParams {
  from: string | undefined
  to: string | undefined
  onRangeChange: (range: { from: string | undefined; to: string | undefined }) => void
}

interface UseDraftDateRangeReturn {
  fromDraft: string | undefined
  toDraft: string | undefined
  isRangeInvalid: boolean
  handleFromChange: (value: string | null) => void
  handleToChange: (value: string | null) => void
}

// The underlying date field reports a partial keystroke the same way as an explicit clear (both are
// `null`), so committing on every change would clear the filter mid-edit. Drafts absorb every
// keystroke immediately for responsive typing; only the value the user settles on — once
// COMMIT_DEBOUNCE_MS passes with no further edits to either bound, and only when that settled pair
// forms a valid range — reaches the caller's onRangeChange. Both bounds commit through a single
// onRangeChange call, never two independent calls: firing two separate commits lets the second one's
// navigation be computed from a stale committed location and silently overwrite the first (AK-6649).
export const useDraftDateRange = ({ from, to, onRangeChange }: UseDraftDateRangeParams): UseDraftDateRangeReturn => {
  const [fromDraft, setFromDraft] = useDraftValue(from)
  const [toDraft, setToDraft] = useDraftValue(to)

  const debouncedFromDraft = useDebounce(fromDraft, COMMIT_DEBOUNCE_MS)
  const debouncedToDraft = useDebounce(toDraft, COMMIT_DEBOUNCE_MS)

  const latestRef = useRef({ from, to, onRangeChange })
  latestRef.current = { from, to, onRangeChange }

  useEffect(() => {
    if (isInvalidDateRange(debouncedFromDraft, debouncedToDraft)) return

    const { from: committedFrom, to: committedTo, onRangeChange: commitRange } = latestRef.current
    if (debouncedFromDraft === committedFrom && debouncedToDraft === committedTo) return

    commitRange({ from: debouncedFromDraft, to: debouncedToDraft })
  }, [debouncedFromDraft, debouncedToDraft])

  return {
    fromDraft,
    toDraft,
    isRangeInvalid: isInvalidDateRange(fromDraft, toDraft),
    handleFromChange: (value) => setFromDraft(value ?? undefined),
    handleToChange: (value) => setToDraft(value ?? undefined),
  }
}
